import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box, Button, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, CircularProgress, Tabs, Tab,
  useMediaQuery, useTheme, InputAdornment
} from '@mui/material';
import { Add, Visibility, ContentCopy, Block, History, Search } from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

function Campaigns() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [campaigns, setCampaigns] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [advertiserSearch, setAdvertiserSearch] = useState('');
  const [filter, setFilter] = useState('active');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const { isPartner, user } = useAuth();

  const [formData, setFormData] = useState({
    name: '', offer_name: '', advertiser_id: '', revenue: '', payout: '', payout_type: 'fixed',
    payout_percentage: '', status: 'active', tracking_type: 'postback',
    postback_url: '', base_destination_url: '', description: '',
  });

  useEffect(() => {
    fetchCampaigns();
    if (!isPartner()) fetchAdvertisers();
  }, []);

  const fetchCampaigns = async () => {
    try {
      if (isPartner()) {
        const response = await axios.get(`/admin/partners/${user.partner_id}/campaigns`);
        const partnerCampaigns = (response.data.data || []).map(c => {
          let partnerPayout;
          if (c.custom_payout) {
            // Custom payout set
            if (c.custom_payout_type === 'percentage') {
              partnerPayout = (parseFloat(c.payout) * parseFloat(c.custom_payout)) / 100;
            } else {
              partnerPayout = parseFloat(c.custom_payout);
            }
          } else {
            // Use default payout
            if (c.payout_type === 'percentage') {
              partnerPayout = (parseFloat(c.payout) * parseFloat(c.payout_percentage || 0)) / 100;
            } else {
              partnerPayout = parseFloat(c.payout_percentage || 0);
            }
          }
          return {
            ...c,
            partner_payout: partnerPayout.toFixed(2)
          };
        });
        setCampaigns(partnerCampaigns);
      } else {
        const response = await axios.get('/admin/campaigns');
        setCampaigns(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdvertisers = async () => {
    try {
      const response = await axios.get('/admin/advertisers');
      setAdvertisers(response.data.data.filter(a => a.status === 'active'));
    } catch (error) {
      console.error('Error fetching advertisers:', error);
    }
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesFilter = filter === 'active' ? campaign.status === 'active' : campaign.status === 'inactive';
    const matchesSearch = searchTerm === '' || 
                          campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (campaign.advertiser_name && campaign.advertiser_name.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesFilter && matchesSearch;
  });

  const handleOpenDialog = (campaign = null) => {
    if (campaign) {
      setEditingCampaign(campaign);
      setFormData({
        name: campaign.name, offer_name: campaign.offer_name || '',
        advertiser_id: campaign.advertiser_id || '', revenue: campaign.revenue || '', revenue: campaign.revenue || 0,
        payout: campaign.payout,
        payout_type: campaign.payout_type || 'fixed', payout_percentage: campaign.payout_percentage || '',
        status: campaign.status, tracking_type: campaign.tracking_type || 'postback',
        postback_url: campaign.postback_url || '', base_destination_url: campaign.base_destination_url || '',
        description: campaign.description || '',
      });
    } else {
      setEditingCampaign(null);
      setFormData({
        name: '', offer_name: '', advertiser_id: '', payout: '', payout_type: 'fixed',
        payout_percentage: '', status: 'active', tracking_type: 'postback',
        postback_url: '', base_destination_url: '', description: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCampaign(null);
    setAdvertiserSearch('');
    setFormData({
      name: '', offer_name: '', advertiser_id: '', payout: '', payout_type: 'fixed',
      payout_percentage: '', status: 'active', tracking_type: 'postback',
      postback_url: '', base_destination_url: '', description: '',
    });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.advertiser_id || !formData.payout) {
      alert('Please fill in all required fields (Name, Advertiser, Payout)');
      return;
    }
    try {
      const payload = {
        name: formData.name, offer_name: formData.offer_name,
        advertiser_id: parseInt(formData.advertiser_id), revenue: Math.round(parseFloat(formData.revenue || 0) * 100) / 100, payout: Math.round(parseFloat(formData.payout) * 100) / 100,
        payout_type: formData.payout_type, payout_percentage: Math.round(parseFloat(formData.payout_percentage || 0) * 100) / 100,
        status: formData.status, tracking_type: formData.tracking_type,
        postback_url: formData.postback_url, base_destination_url: formData.base_destination_url,
        description: formData.description,
      };
      if (editingCampaign) {
        await axios.put(`/admin/campaigns/${editingCampaign.campaign_id}`, payload);
      } else {
        await axios.post('/admin/campaigns', payload);
      }
      setOpenDialog(false);
      setEditingCampaign(null);
      setFormData({
        name: '', offer_name: '', advertiser_id: '', payout: '', payout_type: 'fixed',
        payout_percentage: '', status: 'active', tracking_type: 'postback',
        postback_url: '', base_destination_url: '', description: '',
      });
      await fetchCampaigns();
    } catch (error) {
      console.error('Error saving campaign:', error);
      alert(`Failed to ${editingCampaign ? 'update' : 'create'} campaign: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleDeactivate = async (campaign) => {
    if (!window.confirm(`Are you sure you want to deactivate "${campaign.name}"?\n\nThis will disable all tracking links for this campaign.`)) return;
    try {
      await axios.put(`/admin/campaigns/${campaign.campaign_id}`, {
        ...campaign,
        status: 'inactive',
      });
      await fetchCampaigns();
    } catch (error) {
      console.error('Error deactivating campaign:', error);
      alert(`Failed to deactivate campaign: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleReactivate = async (campaign) => {
    if (!window.confirm(`Are you sure you want to reactivate "${campaign.name}"?`)) return;
    try {
      await axios.put(`/admin/campaigns/${campaign.campaign_id}`, {
        ...campaign,
        status: 'active',
      });
      await fetchCampaigns();
    } catch (error) {
      console.error('Error reactivating campaign:', error);
      alert(`Failed to reactivate campaign: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleCopy = async (campaign) => {
    try {
      const payload = {
        name: `${campaign.name} (Copy)`,
        offer_name: campaign.offer_name || '',
        advertiser_id: campaign.advertiser_id,
        revenue: campaign.revenue || 0,
        payout: campaign.payout,
        payout_type: campaign.payout_type || 'fixed',
        payout_percentage: campaign.payout_percentage || 0,
        status: 'active',
        tracking_type: campaign.tracking_type || 'postback',
        postback_url: campaign.postback_url || '',
        base_destination_url: campaign.base_destination_url || '',
        description: campaign.description || '',
      };
      await axios.post('/admin/campaigns', payload);
      await fetchCampaigns();
    } catch (error) {
      console.error('Error copying campaign:', error);
      alert(`Failed to copy campaign: ${error.response?.data?.error || error.message}`);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Campaigns
        </Typography>
        {!isPartner() && (
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()} size={isMobile ? 'small' : 'medium'}>
            Add Campaign
          </Button>
        )}
      </Box>

      <Card>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
            <Tabs value={filter} onChange={(e, v) => setFilter(v)} sx={{ minHeight: { xs: 36, sm: 48 } }}>
              <Tab label="Active" value="active" sx={{ minHeight: { xs: 36, sm: 48 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }} />
              {!isPartner() && <Tab label="Inactive" value="inactive" sx={{ minHeight: { xs: 36, sm: 48 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }} />}
            </Tabs>
            <TextField
              size="small"
              placeholder="Search campaigns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ width: { xs: '100%', sm: 250 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <TableContainer>
            <Table size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  {!isMobile && <TableCell>ID</TableCell>}
                  <TableCell>Name</TableCell>
                  {!isPartner() && !isMobile && <TableCell>Advertiser</TableCell>}
                  <TableCell>Payout</TableCell>
                  {!isMobile && <TableCell>Status</TableCell>}
                  {!isPartner() && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCampaigns.length > 0 ? (
                  filteredCampaigns.map((campaign) => (
                    <TableRow key={campaign.campaign_id} hover>
                      {!isMobile && <TableCell>{campaign.campaign_id}</TableCell>}
                      <TableCell>
                       <Link
                          to={`/campaigns/${campaign.campaign_id}`}
                          style={{ textDecoration: 'none' }}
                        >
                          <Typography
                            sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 500, fontSize: { xs: '0.8rem', sm: '0.875rem' }, '&:hover': { textDecoration: 'underline' } }}
                          >
                            {campaign.name}
                          </Typography>
                        </Link>
                      </TableCell>
                      {!isPartner() && !isMobile && <TableCell>{campaign.advertiser_name || '-'}</TableCell>}
                      <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        €{isPartner() ? (campaign.partner_payout || campaign.payout) : campaign.payout}
                      </TableCell>
                      {!isMobile && (
                        <TableCell>
                         <Chip 
                          label={campaign.status === 'active' ? 'Active' : 'Inactive'} 
                          color={campaign.status === 'active' ? 'success' : 'error'} 
                          size="small"
                          sx={{ color: 'white' }}
                        />
                        </TableCell>
                      )}
                      {!isPartner() && (
                        <TableCell align="right">
                             <IconButton size="small" component={Link} to={`/campaigns/${campaign.campaign_id}`} title="View Details">
                            <Visibility fontSize="small" />
                          </IconButton>
                          {campaign.status === 'active' && (
                            <>
                              <IconButton size="small" onClick={() => handleCopy(campaign)} title="Copy Campaign">
                                <ContentCopy fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="error" onClick={() => handleDeactivate(campaign)} title="Deactivate">
                                <Block fontSize="small" />
                              </IconButton>
                            </>
                          )}
                          {campaign.status === 'inactive' && (
                            <IconButton size="small" color="success" onClick={() => handleReactivate(campaign)} title="Reactivate">
                              <History fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={isMobile ? 3 : (isPartner() ? 4 : 6)} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>No {filter} campaigns found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog - only for non-partners */}
      {!isPartner() && (
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth fullScreen={isMobile}>
          <DialogTitle>{editingCampaign ? 'Edit Campaign' : 'Add New Campaign'}</DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <TextField fullWidth label="Campaign Name" name="name" value={formData.name} onChange={handleChange} margin="normal" required autoFocus />
              <TextField fullWidth label="Offer Name" name="offer_name" value={formData.offer_name} onChange={handleChange} margin="normal" />
                 <TextField fullWidth select label="Advertiser" name="advertiser_id" value={formData.advertiser_id} onChange={handleChange} margin="normal" required helperText="Required - Partners can be assigned later"
                SelectProps={{
                  MenuProps: {
                    PaperProps: { sx: { maxHeight: 400 } },
                    autoFocus: false
                  }
                }}
              >
                <Box sx={{ position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1, p: 1, borderBottom: '1px solid #eee' }}>
                  <TextField
                    size="small"
                    placeholder="Search advertisers..."
                    fullWidth
                    value={advertiserSearch}
                    onChange={(e) => setAdvertiserSearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18 }} /></InputAdornment>
                    }}
                  />
                </Box>
                <MenuItem value="">Select Advertiser</MenuItem>
                {advertisers
                  .filter(a => advertiserSearch === '' || a.name.toLowerCase().includes(advertiserSearch.toLowerCase()))
                  .map((advertiser) => (
                    <MenuItem key={advertiser.advertiser_id} value={advertiser.advertiser_id}>{advertiser.name}</MenuItem>
                  ))
                }
              </TextField>
              <TextField fullWidth label="Revenue (€)" name="revenue" type="number" value={formData.revenue} onChange={handleChange} margin="normal" required inputProps={{ min: 0, step: 0.01 }} helperText="Amount you receive from the advertiser" />
              <TextField fullWidth label="Partner Payout (€)" name="payout" type="number" value={formData.payout} onChange={handleChange} margin="normal" required inputProps={{ min: 0, step: 0.01 }} helperText="Amount paid to partner" />
              <TextField fullWidth select label="Partner Payout Type" name="payout_type" value={formData.payout_type} onChange={handleChange} margin="normal">
                <MenuItem value="fixed">Fixed Amount (CPA)</MenuItem>
                <MenuItem value="percentage">Percentage (PRV)</MenuItem>
              </TextField>
              <TextField fullWidth label={formData.payout_type === 'fixed' ? 'Partner Payout (€)' : 'Partner Payout (%)'} name="payout_percentage" type="number" value={formData.payout_percentage} onChange={handleChange} margin="normal" inputProps={{ min: 0, step: 0.01 }} helperText={formData.payout_type === 'percentage' && formData.payout && formData.payout_percentage ? `= €${(parseFloat(formData.payout) * parseFloat(formData.payout_percentage) / 100).toFixed(2)} per conversion` : formData.payout_type === 'fixed' ? 'Amount paid to partner' : 'Percentage of revenue paid to partner'} />
              <TextField fullWidth label="Base Destination URL" name="base_destination_url" value={formData.base_destination_url} onChange={handleChange} margin="normal" placeholder="https://example.com/landing" />
              <TextField fullWidth multiline rows={3} label="Description" name="description" value={formData.description} onChange={handleChange} margin="normal" />
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={handleCloseDialog} type="button">Cancel</Button>
              <Button type="submit" variant="contained">{editingCampaign ? 'Update' : 'Create'}</Button>
            </DialogActions>
          </form>
        </Dialog>
      )}
    </Box>
  );
}

export default Campaigns;
